import { useCallback, useState } from "react";
import { useEffect } from "react";
import { PuffLoader } from "react-spinners";

import { Delete } from "@mui/icons-material";
import { Box, Divider, Grid, lighten, Stack, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

import AlertDialog from "../dialogs/alertDialog";
import OmniChatBubble from "../ui/omniChatBubble.js";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import BotpressUserFeedbackDialog from "./botpressUserFeedbackDialog.js";

const BotpressConversationDialog = props => {
  const {
    open,
    onClose,
    onAccept,
    onDelete,
    conversation,
    conversationFeedback,
    updateFeedbackRequest,
    deleteFeedbackRequest,
    userODM,
    dialogTitle = "Transcription",
    okMessage = "Close",
    maxWidth = "md",
    fullWidth = true,
    conversationId = null,
    conversationNumber = null,
    messageId = null,
  } = props;

  const theme = useTheme();

  const onAcceptHandler = () => {
    onAccept();
  };

  const onCloseHandler = () => {
    onClose();
  };

  const [showConfirm, setShowConfirm] = useState(false);

  const [messages, setMessages] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [displayTimeStamp, setDisplayTimeStamp] = useState(true);

  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const handleMessageClick = useCallback(
    message => {
      let existingFeedback = feedback.find(fb => fb.messageId === message.messageId);
      if (existingFeedback == null) {
        existingFeedback = {
          id: null,
          messageId: message.messageId,
          value: "negative",
          comment: "",
          message: message.messageText,
          archived: false,
          status: 1,
          userName: userODM
            ? `${userODM.omniData.givenName} ${userODM.omniData.familyName}`
            : "Unknown User",
          conversationId: conversationId,
          conversationNumber: conversationNumber?.toString(),
        };
      }
      setSelectedFeedback(existingFeedback);
    },
    [feedback, userODM, conversationId, conversationNumber],
  );

  const formatMessage = (message, conversationFeedback) => {
    let messageText = "";

    if (message.type === "text" || message.type === "quick_reply") {
      messageText += message.payload.text;
    } else if (message.type === "choice") {
      messageText += `${message.payload.text}: ${message.payload.options.map(option => option.label).join(" / ")}`;
    } else {
      messageText += `Media message of type ${message.type}`;
    }

    const feedback = conversationFeedback.find(fb => fb.messageId === message.id);

    return {
      messageText: messageText,
      direction: message.direction,
      date: message.createdAt,
      messageId: message.id,
      status: feedback != null ? feedback.status : 0,
      hasFeedback: feedback != null,
      feedback: feedback != null ? feedback.comment : "",
      feedbackValue: feedback != null ? feedback.value : "",
      feedbackId: feedback != null ? feedback.id : null,
    };
  };

  const onDeleteHandler = () => {
    onDelete(conversationId);
  };

  useEffect(() => {
    if (conversation != null && conversationFeedback != null) {
      let allMessages = [];
      allMessages = allMessages.concat(
        conversation.map(message => formatMessage(message, conversationFeedback)),
      );
      setMessages(allMessages.sort((a, b) => new Date(a.date) - new Date(b.date)));
      setFeedback(conversationFeedback);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, conversationFeedback]);

  return (
    <Dialog
      open={open}
      onClose={() => onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        {messages == null && (
          <Grid
            sx={{ textAlign: "center", marginTop: "20px", minimumHeight: "400px" }}
            container
            direction="column"
            alignItems="center"
            spacing={2}
          >
            <Grid>
              <PuffLoader size={100} />
            </Grid>
            <Grid>Loading...</Grid>
          </Grid>
        )}
        {messages != null && (
          <Grid>
            <Grid
              container
              flexDirection={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              spacing={2}
              margin={"0px"}
              padding={"0px"}
            >
              <Grid size={2}>
                <Divider direction={"horizontal"} />
              </Grid>
              <Grid size={"auto"}>
                <Typography
                  variant="overline"
                  color={lighten(theme.palette.commonground.contrastText, 0.5)}
                >
                  Conversation Start
                </Typography>
              </Grid>
              <Grid size={2}>
                <Divider direction={"horizontal"} />
              </Grid>
            </Grid>
            <Grid>
              {messages.map((msg, index) => (
                <Stack
                  direction="column"
                  justifyContent="flex-end"
                  key={msg.messageId || index}
                  spacing={2}
                  size={{ xs: 12 }}
                >
                  <OmniChatBubble
                    key={`chatBubble${index}`}
                    msg={msg}
                    theme={theme}
                    highlightId={messageId}
                    displayTimeStamp={displayTimeStamp}
                    setDisplayTimeStamp={setDisplayTimeStamp}
                    handleMessageClick={handleMessageClick}
                  />
                </Stack>
              ))}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          <OmniResponsiveButton
            icon={<Delete />}
            onClick={() => setShowConfirm(true)}
            buttonText={"Delete"}
            tooltipTitle=""
          />
        </Box>
        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          <Button onClick={onAcceptHandler} color="secondary" variant="contained">
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this conversation?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
      {selectedFeedback != null && (
        <BotpressUserFeedbackDialog
          open={selectedFeedback != null}
          userFeedbackRecord={selectedFeedback}
          dialogTitle={
            selectedFeedback.id ? `Edit Feedback #${selectedFeedback.id}` : "Add Feedback"
          }
          onAccept={theRecord => {
            updateFeedbackRequest({ feedbackRecords: [theRecord] });
            setSelectedFeedback(null);
            // onCloseHandler();
          }}
          onDelete={theRecord => {
            deleteFeedbackRequest({ feedbackIDs: [theRecord.id] });
            setSelectedFeedback(null);
            // onCloseHandler();
          }}
          onClose={() => {
            setSelectedFeedback(null);
          }}
        />
      )}
    </Dialog>
  );
};

export default BotpressConversationDialog;
